// src/components/modals/ViewCustomerModal.jsx
import { X, User, Phone, Calendar, Building, Award, ShoppingBag, DollarSign, Clock, CheckCircle, XCircle } from 'lucide-react'
import { CustomerModel } from '../../utils/customerModel'

const ViewCustomerModal = ({ isOpen, onClose, customer }) => {
  if (!isOpen || !customer) return null

  // Format customer data for display
  const formattedCustomer = {
    ...customer,
    formattedMobile: CustomerModel.formatMobileNumber(customer.mobileNumber),
    formattedPointsBalance: CustomerModel.formatPointsBalance(customer.pointsBalance),
    formattedLifetimePointsEarned: CustomerModel.formatLifetimePointsEarned(customer.lifetimePointsEarned),
    formattedLifetimePointsRedeemed: CustomerModel.formatLifetimePointsRedeemed(customer.lifetimePointsRedeemed),
    formattedLoyaltyPoints: CustomerModel.formatLoyaltyPoints(customer.loyaltyPoints || customer.pointsBalance || 0),
    formattedTotalSpend: CustomerModel.formatTotalSpend(customer.totalSpend || 0),
    formattedDateOfBirth: CustomerModel.formatDate(customer.dateOfBirth),
    formattedLastOrderDate: CustomerModel.formatDate(customer.lastOrderDate),
    formattedJoinDate: CustomerModel.formatDate(customer.createdAt),
    age: CustomerModel.calculateAge(customer.dateOfBirth),
    loyaltyTier: CustomerModel.getLoyaltyTier(customer.pointsBalance, customer.loyaltyPoints)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {/* <div className="p-3 bg-orange-100 rounded-lg">
              <User className="h-6 w-6 text-orange-600" />
            </div> */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Customer Details</h2>
            </div>
          </div>
         <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Customer Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>
            
            {/* Name */}
            <div className="flex items-center space-x-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Customer Name</p>
                <p className="text-gray-900">{customer.name || 'Not available'}</p>
              </div>
            </div>

            {/* Mobile Number */}
            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Mobile Number</p>
                <p className="text-gray-900">{formattedCustomer.formattedMobile}</p>
              </div>
            </div>

            {/* Date of Birth & Age */}
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Date of Birth</p>
                <p className="text-gray-900">
                  {formattedCustomer.formattedDateOfBirth}
                  {formattedCustomer.age && (
                    <span className="text-gray-500 ml-2">({formattedCustomer.age} years old)</span>
                  )}
                </p>
              </div>
            </div>

            {/* Branch */}
            <div className="flex items-center space-x-3">
              <Building className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Branch</p>
                <p className="text-gray-900">
                  {customer.branch?.name || 'Not assigned'}
                </p>
              </div>
            </div>

            {/* Customer Type */}
            <div className="flex items-center space-x-3">
              {customer.customerType === 'vip' ? (
                <svg 
                  className="h-5 w-5 text-yellow-500" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ) : (
                <User className="h-5 w-5 text-gray-400" />
              )}
              <div>
                <p className="text-sm font-medium text-gray-700">Customer Type</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  customer.customerType === 'vip'
                    ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border border-yellow-300 shadow-sm'
                    : 'bg-gray-100 text-gray-800 border border-gray-300'
                }`}>
                  {customer.customerType === 'vip' ? (
                    <>
                      <svg 
                        className="h-3.5 w-3.5 mr-1 text-yellow-600" 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      VIP Customer
                    </>
                  ) : (
                    'Normal Customer'
                  )}
                </span>
              </div>
            </div>

            {/* Account Status */}
            <div className="flex items-center space-x-3">
              {/* {customer.isActive ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )} */}
              <div>
                <p className="text-sm font-medium text-gray-700">Account Status</p>
                <span className={CustomerModel.getStatusBadgeClasses(customer.isActive)}>
                  {customer.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Loyalty & Activity Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Activity & Loyalty</h3>
            
            {/* Loyalty Points & Tier */}
            <div className="flex items-start space-x-3">
              <Award className="h-5 w-5 text-gray-400 mt-1" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">Loyalty Points</p>
                
                {/* Current Points Balance */}
                <div className="flex items-center space-x-2 mb-2">
                  <p className="text-lg font-semibold text-gray-900">{formattedCustomer.formattedPointsBalance}</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${formattedCustomer.loyaltyTier.color}`}>
                    {formattedCustomer.loyaltyTier.name}
                  </span>
                </div>
                
                {/* Points Breakdown */}
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Lifetime Earned:</span>
                    <span className="font-medium text-green-600">{formattedCustomer.formattedLifetimePointsEarned}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Lifetime Redeemed:</span>
                    <span className="font-medium text-red-600">{formattedCustomer.formattedLifetimePointsRedeemed}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="font-medium">Current Balance:</span>
                    <span className="font-semibold text-blue-600">{formattedCustomer.formattedPointsBalance}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Orders */}
            {/* <div className="flex items-center space-x-3">
              <ShoppingBag className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Total Orders</p>
                <p className="text-gray-900">{customer.totalOrders || 0} orders</p>
              </div>
            </div> */}

            {/* Total Spend */}
            {/* <div className="flex items-center space-x-3">
              <DollarSign className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Total Spend</p>
                <p className="text-gray-900">{formattedCustomer.formattedTotalSpend}</p>
              </div>
            </div> */}

            {/* Last Order Date */}
            {/* <div className="flex items-center space-x-3">
              <Clock className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Last Order Date</p>
                <p className="text-gray-900">
                  {formattedCustomer.formattedLastOrderDate}
                </p>
              </div>
            </div> */}
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-6 pt-6 border-t">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-gray-700">Customer ID</p>
              <p className="text-gray-600 font-mono">{customer._id}</p>
            </div>
            
            <div>
              <p className="font-medium text-gray-700">Join Date</p>
              <p className="text-gray-600">{formattedCustomer.formattedJoinDate}</p>
            </div>
            
            {customer.createdBy && (
              <div>
                <p className="font-medium text-gray-700">Created By</p>
                <p className="text-gray-600">{customer.createdBy.username || 'System'}</p>
              </div>
            )}
            
            <div>
              <p className="font-medium text-gray-700">Last Updated</p>
              <p className="text-gray-600">{CustomerModel.formatDate(customer.updatedAt)}</p>
            </div>
          </div>
        </div>

        {/* Customer Statistics Summary */}
        {/* {(customer.totalOrders > 0 || customer.totalSpend > 0 || customer.lifetimePointsEarned > 0) && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Customer Summary</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4"> */}
              {/* Average Order Value */}
              {/* {customer.totalOrders > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Average Order Value</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {CustomerModel.formatTotalSpend(customer.totalSpend / customer.totalOrders)}
                  </p>
                </div>
              )} */}
              
              {/* Loyalty Tier Progress */}
              {/* <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Loyalty Tier</p>
                <p className="text-lg font-semibold text-gray-900">{formattedCustomer.loyaltyTier.name}</p>
              </div>
               */}
              {/* Points Utilization Rate */}
              {/* {customer.lifetimePointsEarned > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">Points Usage Rate</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {Math.round((customer.lifetimePointsRedeemed / customer.lifetimePointsEarned) * 100)}%
                  </p>
                </div>
              )} 
            </div>
          </div>
        )}
          */}

        {/* Close Button */}
        <div className="flex justify-end mt-6 pt-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewCustomerModal